import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { getUser, postNpsFeedback } from '@/core/api';
import { APIEventsEnum, useAppDispatch, useSendEvent } from '../../hooks';
import { setUser } from '@/core/store/user/userSlice';
import { useNPSModal } from './useNPSModal';
import * as Sentry from '@sentry/browser';
import useNotification from '@/hooks/useNotification';
import AnimateHeight from 'react-animate-height';

export const NPSSurvey = () => {
  const { t } = useTranslation();
  const [score, setScore] = useState<number>();
  const [isLoading, setIsLoading] = useState(false);
  const { sendEvent } = useSendEvent();
  const { showNotification } = useNotification();

  const { isModalOpened, closeModal, event } = useNPSModal();

  useEffect(() => {
    if (isModalOpened) {
      sendEvent(APIEventsEnum.view_cta, {
        block: 'NPSModal',
        event,
      });
      setScore(undefined);
    }
    // eslint-disable-next-line
  }, [isModalOpened]);

  const dispatch = useAppDispatch();

  const onClick = (v: number) => () => {
    setIsLoading(true);
    postNpsFeedback(v)
      .then(() => {
        return getUser();
      })
      .then((res) => {
        dispatch(setUser(res));
        closeModal();
      })
      .catch((err) => {
        Sentry.captureException(err);
        showNotification({
          text: 'Something went wrong, please try again',
          type: 'error',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <AnimateHeight
      duration={250}
      height={isModalOpened ? 'auto' : 0}
      easing={'cubic-bezier(0.4, 0, 0.2, 1)'}
    >
      <div
        data-testid='NPSSurvey'
        className='flex flex-wrap justify-between gap-x-6 gap-y-2 bg-white px-6 py-4 lg:flex-nowrap'
      >
        <div className='min-w-[263px]'>
          <h2 className='Title--sm mb-2 font-extrabold'>
            {t('How likely are you to recommend countX to others?')}
          </h2>
          <div>
            {t(
              'If you share your feedback now, this request will disappear for 90 days'
            )}
          </div>
        </div>

        <div className='grow-1'>
          <div className='flex flex-wrap items-center justify-between gap-x-1 pb-2'>
            <div className='flex items-center gap-x-1'>
              {[...Array(11)].map((_, i) => (
                <button
                  key={i}
                  className={`flex h-12 w-12 cursor-pointer items-center justify-center rounded-2xl border text-center font-bold leading-none transition-colors hover:border-green hover:bg-green hover:text-white ${
                    score === i
                      ? 'border-green bg-green text-white'
                      : 'border-gray-500 bg-white text-dark'
                  }`}
                  onClick={onClick(i)}
                  disabled={isLoading}
                  data-testid={`nps-score-${i}`}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>
          <div className='flex justify-between text-sm text-gray-700'>
            <span>{t('Not likely at all')}</span>
            <span>{t('Extremely likely')}</span>
          </div>
        </div>
      </div>
    </AnimateHeight>
  );
};
